import type { EuIdentifiers, UdiDiRow, UdiIssuingEntity } from './api';
import { BASIC_UDI_DIS_MAX, UDI_DI_ROWS_MAX } from './field-limits';
import { selectedCountries } from './submission-pathways';

/** A device's EU identifiers — a Basic UDI-DI per device model ("the primary identifier of a device
 *  model", Annex VI Part C; on the DoC and in EUDAMED — most products have one) and one row per
 *  UDI-DI (per configuration / packaging level), each belonging to one Basic UDI-DI. MDR / IVDR
 *  Art. 27; RA acceptance checklist B.1. Asked in the product's Countries & Regulatory
 *  Classification; the rows work like the company account's sites. */
export const EMPTY_EU_IDENTIFIERS: EuIdentifiers = { basic_udi_dis: [], issuing_entity: null, eudamed_status: null, udi_dis: [] };
export const BLANK_UDI_ROW: UdiDiRow = { udi_di: '', configuration: '', packaging_level: '', basic_udi_di: '' };

/** The designated issuing entities, in the order the dropdown offers them. */
export const UDI_ISSUING_ENTITIES: readonly UdiIssuingEntity[] = ['GS1', 'HIBCC', 'ICCBBA', 'IFA'];

/** Only a device with the EU among its countries — the Basic UDI-DI is an EU identifier (the US
 *  uses GUDID), and drugs / biologics have none. */
export function showsEuIdentifiers(productType: string | null, pathways: Record<string, string>): boolean {
  return productType === 'device' && selectedCountries(pathways).includes('EU');
}

/** The Basic UDI-DIs as kept: trimmed, blanks dropped, a repeat kept once. */
export function keptBasicUdiDis(ids: EuIdentifiers): string[] {
  return [...new Set(ids.basic_udi_dis.map((b) => b.trim()).filter(Boolean))];
}

/** What is kept, as the server keeps it: trimmed; only the rows that carry their UDI-DI; with one
 *  Basic UDI-DI every row belongs to it, with several a row keeps only a link to a listed one. */
export function keptEuIdentifiers(ids: EuIdentifiers): EuIdentifiers {
  const basics = keptBasicUdiDis(ids);
  const only = basics.length === 1 ? basics[0]! : '';
  return {
    basic_udi_dis: basics,
    issuing_entity: ids.issuing_entity ?? null,
    eudamed_status: ids.eudamed_status ?? null,
    udi_dis: ids.udi_dis
      .map((r) => {
        const link = r.basic_udi_di.trim();
        return {
          udi_di: r.udi_di.trim(),
          configuration: r.configuration.trim(),
          packaging_level: r.packaging_level.trim(),
          basic_udi_di: only || (basics.includes(link) ? link : ''),
        };
      })
      .filter((r) => r.udi_di),
  };
}

/** The UDI-DI rows that belong to Basic UDI-DI ``index`` — noted when editing that field starts, so the
 *  edit moves exactly these rows (code review 2026-10-22: relinking by value on every keystroke merged two
 *  models' rows when their values briefly matched, and clearing a field dropped its links). With one Basic
 *  UDI-DI every row is its own, linked or not yet. */
export function linkedRows(ids: EuIdentifiers, index: number): number[] {
  const basic = ids.basic_udi_dis[index] ?? '';
  const only = ids.basic_udi_dis.length <= 1;
  return ids.udi_dis.flatMap((r, i) =>
    (basic && r.basic_udi_di === basic) || (only && !r.basic_udi_di.trim()) ? [i] : [],
  );
}

/** Basic UDI-DI ``index`` set to ``value``; the ``rows`` noted by ``linkedRows`` follow it. An index one
 *  past the end (the single empty field shown for an empty list) adds it. */
export function withBasicUdiDi(ids: EuIdentifiers, index: number, value: string, rows: readonly number[]): EuIdentifiers {
  const basic_udi_dis = [...ids.basic_udi_dis];
  basic_udi_dis[index] = value;
  const mine = new Set(rows);
  return {
    ...ids,
    basic_udi_dis,
    udi_dis: ids.udi_dis.map((r, i) => (mine.has(i) ? { ...r, basic_udi_di: value } : r)),
  };
}

/** A second (or further) Basic UDI-DI: rows that belonged to the only one implicitly are linked to it
 *  first, so they don't turn "Not chosen" (and save unlinked) once there are two. */
export function withAnotherBasicUdiDi(ids: EuIdentifiers): EuIdentifiers {
  const only = ids.basic_udi_dis.length === 1 ? (ids.basic_udi_dis[0] ?? '').trim() : '';
  return {
    ...ids,
    basic_udi_dis: [...ids.basic_udi_dis, ''],
    udi_dis: only ? ids.udi_dis.map((r) => (r.basic_udi_di.trim() ? r : { ...r, basic_udi_di: only })) : ids.udi_dis,
  };
}

/** Basic UDI-DI ``index`` removed — its UDI-DIs are left to be linked again. */
export function withoutBasicUdiDi(ids: EuIdentifiers, index: number): EuIdentifiers {
  const gone = ids.basic_udi_dis[index];
  return {
    ...ids,
    basic_udi_dis: ids.basic_udi_dis.filter((_, i) => i !== index),
    udi_dis: ids.udi_dis.map((r) => (gone && r.basic_udi_di === gone ? { ...r, basic_udi_di: '' } : r)),
  };
}

const EUDAMED_SUMMARY = { registered: 'Registered', not_registered: 'Not yet registered' } as const;
/** Up to this many codes are listed in the summary; more are counted. */
const SUMMARY_LIST_MAX = 3;

export interface SummaryItem {
  label: string;
  value: string;
}

function listed(singular: string, plural: string, codes: string[]): SummaryItem | null {
  if (codes.length === 0) return null;
  if (codes.length === 1) return { label: singular, value: codes[0]! };
  return {
    label: plural,
    value: codes.length <= SUMMARY_LIST_MAX ? codes.join(', ') : `${codes.length} entered`,
  };
}

/** What the EU identifiers box shows while closed: each entered value under its label ("Basic UDI-DI:
 *  7350012CITLALLIPGR2K"), nothing that isn't entered (Marc, 2026-10-21). */
export function euIdentifiersSummary(ids: EuIdentifiers): SummaryItem[] {
  const kept = keptEuIdentifiers(ids);
  const items: (SummaryItem | null)[] = [
    kept.issuing_entity ? { label: 'Issuing entity', value: kept.issuing_entity } : null,
    kept.eudamed_status ? { label: 'EUDAMED registration', value: EUDAMED_SUMMARY[kept.eudamed_status] } : null,
    listed('Basic UDI-DI', 'Basic UDI-DIs', kept.basic_udi_dis),
    listed('UDI-DI', 'UDI-DIs', kept.udi_dis.map((r) => r.udi_di)),
  ];
  return items.filter((i): i is SummaryItem => i !== null);
}

/** Another UDI-DI row can be added — up to the cap the server enforces. */
export function canAddUdiDi(ids: EuIdentifiers): boolean {
  return ids.udi_dis.length < UDI_DI_ROWS_MAX;
}

/** Another Basic UDI-DI can be added — up to the cap the server enforces. */
export function canAddBasicUdiDi(ids: EuIdentifiers): boolean {
  return ids.basic_udi_dis.length < BASIC_UDI_DIS_MAX;
}

export function hasEuIdentifiers(ids: EuIdentifiers): boolean {
  const kept = keptEuIdentifiers(ids);
  return (
    kept.basic_udi_dis.length > 0 || kept.issuing_entity !== null || kept.eudamed_status !== null || kept.udi_dis.length > 0
  );
}

/** Whether saving ``draft`` would change what is on file. */
export function euIdentifiersChanged(draft: EuIdentifiers, saved: EuIdentifiers): boolean {
  return JSON.stringify(keptEuIdentifiers(draft)) !== JSON.stringify(keptEuIdentifiers(saved));
}
